import { useState } from "react";

import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";
import CircularProgress from "@mui/material/CircularProgress";

import { ChevronDownIcon, colors } from "@dotnet/telematics-design-system";

import { useMenu } from "components/Actions/ActionBar/hooks/useMenu";
import ListChoice from "components/DesignComponents/ListChoice";
import Menu from "components/DesignComponents/Menu";
import {
    leftAnchorOrigin,
    leftTransformOrigin
} from "components/DesignComponents/Menu/shared/properties";
import Tag, { TAG_COLORS, TAG_SIZES } from "components/DesignComponents/Tag";
import { TooltipGeneral } from "components/DesignComponents/Tooltips/TooltipGeneral";
import { TOOLTIP_PLACEMENT } from "components/DesignComponents/Tooltips/types/enums";
import { getFeatureSettingsName } from "components/TableDetails/DeviceDetails/Configuration/Overview/functions";
import { noop } from "shared/functions";
import { useApi } from "shared/hooks/useApi";
import { usePermissions } from "shared/hooks/usePermissions";
import { body4Bold } from "shared/styles/typography";
import {
    CONFIGURATION_FEATURE_SETTINGS_MODES,
    ENDPOINTS
} from "shared/types/enums";

import { SYNC_MODE_OPTIONS, SYNC_MODES } from "./constants";
import { SyncModeWithSelectionProps } from "./types";

const SyncModeWithSelection = ({
    imei,
    type,
    mode
}: SyncModeWithSelectionProps) => {
    const { gray700, textPlaceholder } = colors;

    const { t } = useTranslation();
    const { anchorEl, handleOpenMenu, handleCloseMenu } = useMenu();
    const { updateData, handleResponse } = useApi();
    const {
        isUpdateDeviceNotAllowed,
        PERMISSIONS,
        getNoPermissionTooltipTitle
    } = usePermissions();

    const [isLoading, setIsLoading] = useState(false);
    const [selectedMode, setSelectedMode] = useState(mode);

    const IconLeft = SYNC_MODE_OPTIONS[selectedMode].icon;
    const iconColor = SYNC_MODE_OPTIONS[selectedMode].iconColor;

    const handleSubmit = async (
        newMode: CONFIGURATION_FEATURE_SETTINGS_MODES
    ) => {
        handleCloseMenu();

        try {
            setIsLoading(true);

            await updateData(ENDPOINTS.Devices, imei, {
                feature_settings: {
                    [getFeatureSettingsName(type)]: newMode
                }
            });

            setSelectedMode(newMode);
        } catch (error) {
            handleResponse(error);
        }

        setIsLoading(false);
    };

    const isDisabled = isUpdateDeviceNotAllowed || isLoading;

    return (
        <>
            <TooltipGeneral
                title={getNoPermissionTooltipTitle(PERMISSIONS.DeviceUpdate)}
                placement={TOOLTIP_PLACEMENT.Top}
            >
                <span css={css({ display: "inline-flex" })}>
                    <Tag
                        size={TAG_SIZES.Medium}
                        color={TAG_COLORS.White}
                        iconLeft={<IconLeft />}
                        iconRight={
                            isLoading ? (
                                <CircularProgress
                                    size={12}
                                    css={css({ color: gray700 })}
                                />
                            ) : (
                                <ChevronDownIcon />
                            )
                        }
                        iconLeftColor={iconColor}
                        title={t(
                            `Table details##device details##configuration##mode titles##${selectedMode}`
                        )}
                        disabled={isDisabled}
                        onClick={handleOpenMenu}
                    />
                </span>
            </TooltipGeneral>

            <Menu
                open={Boolean(anchorEl)}
                onClose={handleCloseMenu}
                anchorEl={anchorEl}
                anchorOrigin={leftAnchorOrigin}
                transformOrigin={leftTransformOrigin}
                css={css({
                    "& .MuiMenu-paper": {
                        width: "290px"
                    }
                })}
            >
                <li
                    css={[
                        body4Bold,
                        {
                            color: textPlaceholder,
                            textTransform: "uppercase",
                            padding: "14px 16px"
                        }
                    ]}
                >
                    {t(
                        "Table details##device details##configuration##sync status"
                    )}
                </li>

                {SYNC_MODES.map(syncMode => {
                    const Icon = SYNC_MODE_OPTIONS[syncMode].icon;
                    const iconColor = SYNC_MODE_OPTIONS[syncMode].iconColor;
                    const isSelected = syncMode === selectedMode;

                    return (
                        <ListChoice
                            key={syncMode}
                            title={t(
                                `Table details##device details##configuration##mode titles##${syncMode}`
                            )}
                            description={t(
                                `Table details##device details##configuration##mode descriptions##${syncMode}`
                            )}
                            iconLeft={<Icon css={css({ color: iconColor })} />}
                            selected={isSelected}
                            onClick={() =>
                                isSelected ? noop() : handleSubmit(syncMode)
                            }
                            css={css({
                                pointerEvents: isSelected ? "none" : "auto"
                            })}
                        />
                    );
                })}
            </Menu>
        </>
    );
};

export default SyncModeWithSelection;
